import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { useBatchVerification } from '@/hooks/useBatchVerification'
import type { BatchVerificationResult } from '@/services/trustedVerifiersService'

interface BatchVerificationContextType {
    getVerification: (identifier: string) => BatchVerificationResult | null
    isLoading: boolean
}

const BatchVerificationContext = createContext<BatchVerificationContextType | null>(null)

/** Returns the batch verification context, or null when not inside a BatchVerificationProvider. */
export function useBatchVerificationContext(): BatchVerificationContextType | null {
    return useContext(BatchVerificationContext)
}

export function BatchVerificationProvider({
    children,
    dids,
}: {
    children: ReactNode
    dids: string[]
}) {
    const { verificationMap, isLoading } = useBatchVerification(dids)

    const getVerification = useCallback(
        (identifier: string): BatchVerificationResult | null => {
            if (Object.prototype.hasOwnProperty.call(verificationMap, identifier)) {
                return verificationMap[identifier]
            }
            return null
        },
        [verificationMap]
    )

    const contextValue = useMemo(
        () => ({ getVerification, isLoading }),
        [getVerification, isLoading]
    )

    return (
        <BatchVerificationContext.Provider value={contextValue}>
            {children}
        </BatchVerificationContext.Provider>
    )
}
